import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import { SAAS, SOFTWARE, StorageQuantity, Tiles } from '../constants/default';

/**
 * Calculate the estimated price
 *
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} connectionQty : Number of connection
 * @param {number} mapQty : Number of map
 * @param {number} documentQty : Number of document
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  country: string;
  localeCode: string;
  mvs: number;
  solution: string;
  tileValueSelected: string;
  users: number;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

interface PriceParts {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: ScaleQtyPrice[];
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}

const partNumber = {
  Essentials: 'D0CV9ZX',
  Standard: 'D0CVCZX',
  Premium: 'D0CV7ZX',
  Dspm: {
    Datastores: 'D0GEVZX',
    Users: 'D0GEXZX',
  },
  Software: 'D0A2GZX',
  Storage: 'D0DNSZX',
};

const partsToQuery = [
  {
    partNumber: partNumber.Essentials,
  },
  {
    partNumber: partNumber.Standard,
  },
  {
    partNumber: partNumber.Premium,
  },
  {
    partNumber: partNumber.Software,
  },
  {
    partNumber: partNumber.Dspm.Datastores,
  },
  {
    partNumber: partNumber.Dspm.Users,
  },
  {
    partNumber: partNumber.Storage,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({
  currency,
  country,
  localeCode,
  mvs,
  solution,
  tileValueSelected,
  users,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [dsPrices, setDsPrice] = useCurrencyWithSymbol(currency, localeCode, 0);
  const [totalStoragePrice, setTotalStoragePrice] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );

  const [swPrice, setSWPrice] = useState(0);
  const [essentialPrice, setEssentialPrice] = useState(0);
  const [standardPrice, setStandardPrice] = useState(0);
  const [premiumPrice, setPremiumPrice] = useState(0);
  const [storagePrice, setStoragePrice] = useState(0);
  const [storageQty, setStorageQty] = useState(0);

  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);

  const calculatePrice = (price: number, qty: number) => {
    return price * qty;
  };

  const calculateEstimates = () => {
    let price = 0;
    if (solution === SAAS) {
      if (tileValueSelected === Tiles.essentials) {
        price = calculatePrice(essentialPrice, mvs);
      } else if (tileValueSelected === Tiles.standard) {
        price = calculatePrice(standardPrice, mvs);
      } else {
        price = 0;
      }
    } else if (solution === SOFTWARE) {
      price = calculatePrice(swPrice, mvs);
    }
    return price;
  };

  const calculateStorageQty = () => {
    let quantityInGB = 0;
    if (tileValueSelected === Tiles.essentials) {
      quantityInGB = StorageQuantity.essentials * mvs;
    } else if (tileValueSelected === Tiles.standard) {
      quantityInGB = StorageQuantity.standard * mvs;
    }
    return quantityInGB;
  };

  const calculateStoragePrice = () => {
    return storagePrice * calculateStorageQty();
  };

  // calculate the total cost
  const calculateTotalCost = (
    tileValueSelected: string,
    priceForDatastores: number,
    priceForStorage: number,
  ) => {
    let totalPrice = 0;
    if (solution === SAAS) {
      totalPrice = priceForDatastores + priceForStorage;
    } else if (solution === SOFTWARE) {
      totalPrice = priceForDatastores;
    }
    return totalPrice;
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      // setting software price
      const softwarePrice = products.find(
        (i: PriceParts) => i.partNumber === partNumber.Software,
      )?.scaleQtyPrice[0].price;
      setSWPrice(softwarePrice ? softwarePrice : 0);

      //setting essentials prices
      const essentialPrice = products.find(
        (i: PriceParts) => i.partNumber === partNumber.Essentials,
      )?.scaleQtyPrice[0].price;
      setEssentialPrice(essentialPrice ? essentialPrice * 12 : 0);

      //setting standard prices
      const stdPrice = products.find(
        (i: PriceParts) => i.partNumber === partNumber.Standard,
      )?.scaleQtyPrice[0].price;
      setStandardPrice(stdPrice ? stdPrice * 12 : 0);

      //setting premium prices
      const premiumPrice = products.find(
        (i: PriceParts) => i.partNumber === partNumber.Premium,
      )?.scaleQtyPrice[0].price;
      setPremiumPrice(premiumPrice ? premiumPrice * 12 : 0);

      //setting storage prices
      const storagePrice = products.find(
        (i: PriceParts) => i.partNumber === partNumber.Storage,
      )?.scaleQtyPrice[0].price;
      setStoragePrice(storagePrice ? storagePrice * 12 : 0); // changed as per QRADAR-1116
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const priceForDatastores = calculateEstimates();
  const priceForStorage = calculateStoragePrice();
  const storageQtyInGB = calculateStorageQty();

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency, localeCode, country]);

  useEffect(() => {
    setDsPrice(priceForDatastores);
    setTotalStoragePrice(priceForStorage);
    setStorageQty(storageQtyInGB);
    setTotalCost(
      calculateTotalCost(
        tileValueSelected,
        priceForDatastores,
        priceForStorage,
      ),
    );
  }, [
    mvs,
    tileValueSelected,
    solution,
    essentialPrice,
    swPrice,
    standardPrice,
    premiumPrice,
    users,
    storagePrice,
    storageQty,
  ]);

  return [
    totalCost,
    dsPrices,
    totalStoragePrice,
    storageQty,
    loading,
    fetchingError,
  ];
};
